// src/pages/shelbula/workspaces/ConfirmationModals/CreateRepoModal.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GitBranchPlus, X, Loader } from 'lucide-react';

const CreateRepoModal = ({ isOpen, onClose, onConfirm, isCreatingRepo, defaultRepoName }) => {
    const [repoName, setRepoName] = useState('');
    const [isPrivate, setIsPrivate] = useState(true);

    useEffect(() => {
        if (isOpen) {
            setRepoName(defaultRepoName || '');
            setIsPrivate(true);
        }
    }, [isOpen, defaultRepoName]);

    const handleConfirm = (e) => {
        e.preventDefault();
        if (repoName.trim()) {
            onConfirm({ repoName: repoName.trim(), isPrivate });
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="relative w-full max-w-lg bg-surface border border-border-color rounded-xl shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <form onSubmit={handleConfirm}>
                            <div className="flex items-start gap-4 p-4">
                                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-surface-dark">
                                    <GitBranchPlus size={20} className="text-primary" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-on-surface">Create GitHub Repository</h3>
                                    <p className="text-sm text-on-surface-dim mt-2">
                                        A new repository will be created on GitHub and linked to this workspace. The current files will be pushed to a new branch.
                                    </p>
                                    <div className="mt-4 space-y-4">
                                        <div>
                                            <label htmlFor="repoName" className="block text-xs font-medium text-on-surface-dim mb-1.5">Repository Name</label>
                                            <input
                                                id="repoName"
                                                type="text"
                                                value={repoName}
                                                onChange={(e) => setRepoName(e.target.value)}
                                                placeholder="e.g., my-awesome-project"
                                                className="w-full px-3 py-2 bg-background-darker border border-border-color rounded-md text-on-surface placeholder:text-on-surface-dim/50 focus:outline-none focus:ring-1 focus:ring-primary text-sm"
                                                required
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input
                                                id="isPrivate"
                                                type="checkbox"
                                                checked={isPrivate}
                                                onChange={(e) => setIsPrivate(e.target.checked)}
                                                className="h-4 w-4 rounded bg-surface-dark border-border-color text-primary focus:ring-primary focus:ring-offset-surface"
                                            />
                                            <label htmlFor="isPrivate" className="text-sm text-on-surface">
                                                Private repository
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <button type="button" onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full text-on-surface-dim hover:bg-surface-dark transition-colors">
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="flex justify-end gap-3 p-4 bg-background-dark border-t border-border-color">
                                <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold rounded-md bg-surface-dark hover:bg-surface-raised text-on-surface transition-colors">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!repoName.trim() || isCreatingRepo}
                                    className="px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white hover:bg-primary-dark transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isCreatingRepo ? (
                                        <>
                                            <Loader size={14} className="animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <GitBranchPlus size={14} />
                                            Create Repository
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default CreateRepoModal;